import { useEffect, useState } from "react";
import { X, Gauge, Clock, Info, AlertTriangle, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const SRC_LABEL = { gps: "GPS", gps_calibrated: "GPS ajusté", can: "CAN", manual: "Manuel", ignition: "Contact", rpm: "Régime moteur", calibrated: "Contact ajusté", device: "Boîtier" };

// P0.2 — Mise à jour / calibration explicite d'un compteur (km ou heures moteur).
// Passe EXCLUSIVEMENT par POST /vehicles/{id}/meter-reading (ancrage) puis, si un facteur
// candidat est proposé, par POST /vehicles/{id}/meter-calibration/accept. N'écrit jamais
// directement odometer_km / engine_hours.
export default function MeterCalibrationModal({ vehicle, metric: metricProp, onClose, onSaved }) {
  const metric = metricProp || (vehicle?.usage_metric === "engine_hours" ? "engine_hours" : "odometer");
  const isH = metric === "engine_hours";
  const unit = isH ? "h" : "km";
  const eff = isH ? (vehicle?.engine_hours || 0) : (vehicle?.odometer_km || 0);
  const raw = isH ? vehicle?.engine_hours_raw : vehicle?.odometer_raw_km;
  const src = isH ? vehicle?.engine_hours_source : vehicle?.odometer_source;
  const factor = isH ? (vehicle?.eh_factor ?? 1) : (vehicle?.odo_factor ?? 1);
  const anchorRaw = isH ? vehicle?.eh_anchor_raw : vehicle?.odo_anchor_raw;
  const isCan = isH ? (src === "can" || src === "device") : (src === "can");

  const rawVal = raw != null ? raw : eff;
  const rawProgress = anchorRaw != null ? Math.max(0, rawVal - anchorRaw) : 0;

  const fmt = (n) => isH
    ? `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} h`
    : `${Math.round(n || 0).toLocaleString("fr-FR")} km`;

  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastReading, setLastReading] = useState(null);
  const [pending, setPending] = useState(null); // {factor, confidence, raw_progress, factor_current}
  const [anomaly, setAnomaly] = useState(false);
  const [belowConfirm, setBelowConfirm] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/vehicles/${vehicle.id}/meter-readings?metric=${metric}`)
      .then((r) => { if (alive) setLastReading((r.data || []).find((x) => x.source === "physical") || (r.data || [])[0] || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [vehicle.id, metric]);

  const submitReading = async (action = "normal") => {
    if (value === "" || isNaN(Number(value))) return;
    const real = Number(value);
    setBusy(true);
    try {
      const r = await api.post(`/vehicles/${vehicle.id}/meter-reading`, { metric, real_value: real, action });
      const d = r.data || {};
      if (d.anomaly) {
        setAnomaly(true);  // relevé enregistré (ancrage) mais écart hors plage : aucune proposition
        return;
      }
      if (d.candidate_factor) {
        toast.success("Relevé enregistré — un coefficient est proposé.");
        setPending({ factor: d.candidate_factor, confidence: d.candidate_confidence, raw_progress: d.raw_progress, factor_current: d.factor_current ?? factor });
        return;
      }
      toast.success(action === "correction" ? "Correction enregistrée (ancrage)." : "Compteur mis à jour (ancrage enregistré).");
      onSaved?.(); onClose?.();
    } catch (e) {
      if (e?.response?.status === 409) {
        setBelowConfirm(true);  // valeur < dernier relevé physique -> correction explicite
        return;
      }
      const det = e?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : (det?.error || "Relevé refusé"));
    } finally { setBusy(false); }
  };

  const applyFactor = async () => {
    setBusy(true);
    try {
      await api.post(`/vehicles/${vehicle.id}/meter-calibration/accept`, { metric, factor: pending.factor, confirm: true });
      toast.success("Coefficient appliqué aux valeurs futures.");
      onSaved?.(); onClose?.();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : (det?.error || "Application refusée"));
    } finally { setBusy(false); }
  };

  const keepFactor = () => { toast.info("Relevé conservé, coefficient inchangé."); onSaved?.(); onClose?.(); };

  const INP = "w-full bg-secondary/60 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-[#17A2B8]";
  const Row = ({ label, value, testid, icon: Icon }) => (
    <div className="flex items-center justify-between py-1" data-testid={testid}>
      <span className="text-[12px] text-muted-foreground flex items-center gap-1.5">{Icon && <Icon className="w-3.5 h-3.5" />}{label}</span>
      <span className="text-sm font-semibold data">{value}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[2100] flex items-center justify-center bg-black/60 p-4" data-testid="meter-calib-modal" onClick={onClose}>
      <div className="w-full max-w-md bg-card border border-border rounded-xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Gauge className="w-4 h-4 text-[#17A2B8]" />
            <h3 className="text-sm font-bold">{isH ? "Mettre à jour les heures moteur" : "Mettre à jour / calibrer le kilométrage"}</h3>
          </div>
          <button onClick={onClose} data-testid="meter-calib-close" className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-3">
          <div className="rounded-md border border-border p-3">
            <Row label={isH ? "Heures affichées" : "Kilométrage affiché"} value={fmt(eff)} testid="calib-current-eff" />
            <Row label="Source actuelle" value={SRC_LABEL[src] || "GPS"} testid="calib-source" />
            <Row label="Coefficient actuel" value={Number(factor).toLocaleString("fr-FR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} testid="calib-factor-current" />
            <Row label="Progression brute depuis le dernier relevé" value={fmt(rawProgress)} testid="calib-raw-progress" icon={Info} />
            {lastReading && (
              <Row label="Dernier relevé manuel" testid="calib-last-reading" icon={Clock}
                value={`${fmt(lastReading.real_value)} · ${new Date(lastReading.timestamp).toLocaleDateString("fr-FR")}`} />
            )}
          </div>

          {isCan && (
            <div className="rounded-md border border-[#F5821F]/40 bg-[#F5821F]/10 p-2.5 text-[12px] flex gap-2" data-testid="calib-can-note">
              <AlertTriangle className="w-4 h-4 shrink-0 text-[#F5821F]" />
              <span>Source <b>CAN</b> : aucun coefficient GPS n'est appliqué. Vous corrigez uniquement la valeur affichée (ancrage), elle restera persistante.</span>
            </div>
          )}

          {!pending ? (
            <>
              <div>
                <label className="text-[12px] text-muted-foreground mb-1 block">{isH ? "Heures réelles constatées au compteur" : "Kilométrage réel constaté au compteur"}</label>
                <input type="number" step={isH ? "0.1" : "1"} autoFocus value={value} data-testid="calib-input"
                  onChange={(e) => { setValue(e.target.value); setAnomaly(false); setBelowConfirm(false); }} placeholder={isH ? "Ex. 1250.5" : "Ex. 85000"} className={INP} />
              </div>
              {anomaly && (
                <div className="rounded-md border border-[#EF4444]/50 bg-[#EF4444]/10 p-2.5 text-[12px] flex gap-2" data-testid="calib-anomaly">
                  <AlertTriangle className="w-4 h-4 shrink-0 text-[#EF4444]" />
                  <span>Relevé enregistré, mais l'écart est <b>hors plage acceptable (0,80–1,25)</b> : aucun coefficient proposé, l'ancien est conservé. Vérifiez le compteur, le boîtier ou la saisie.</span>
                </div>
              )}
              {belowConfirm && (
                <div className="rounded-md border border-[#F5821F]/50 bg-[#F5821F]/10 p-2.5 text-[12px] space-y-2" data-testid="calib-below-confirm">
                  <div className="flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0 text-[#F5821F]" /><span>La valeur saisie est <b>inférieure au dernier relevé</b>. Confirmez une correction (remise à niveau du compteur, sans coefficient).</span></div>
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => setBelowConfirm(false)} data-testid="calib-below-cancel" className="text-sm text-muted-foreground px-3 py-1.5">Annuler</button>
                    <button disabled={busy} onClick={() => submitReading("correction")} data-testid="calib-below-apply" className="text-sm font-bold bg-[#F5821F] text-white px-3 py-1.5 rounded-md disabled:opacity-40">Confirmer la correction</button>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-1">
                <button onClick={onClose} data-testid="calib-cancel" className="text-sm text-muted-foreground px-3 py-2">{anomaly ? "Fermer" : "Annuler"}</button>
                {anomaly ? (
                  <button onClick={() => { onSaved?.(); onClose?.(); }} data-testid="calib-anomaly-ok"
                    className="text-sm font-bold bg-[#17A2B8] text-white px-4 py-2 rounded-md">Terminer</button>
                ) : (
                  <button disabled={busy || value === "" || belowConfirm} onClick={() => submitReading("normal")} data-testid="calib-submit"
                    className="text-sm font-bold bg-[#17A2B8] text-white px-4 py-2 rounded-md disabled:opacity-40">Enregistrer le relevé</button>
                )}
              </div>
            </>
          ) : (
            <div className="rounded-md border border-[#17A2B8]/40 bg-[#17A2B8]/10 p-3 space-y-2" data-testid="calib-factor-panel">
              <div className="flex items-center gap-2 text-sm font-bold text-[#17A2B8]"><CheckCircle2 className="w-4 h-4" />Coefficient proposé</div>
              <Row label="Coefficient actuel" value={Number(pending.factor_current || 1).toLocaleString("fr-FR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} testid="calib-prop-current" />
              <Row label="Coefficient calculé" value={Number(pending.factor).toLocaleString("fr-FR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} testid="calib-prop-new" />
              <Row label="Fiabilité" testid="calib-prop-confidence"
                value={pending.confidence === "consolidated" ? `Consolidé (≥ ${isH ? 50 : 500} ${unit})` : `Provisoire (< ${isH ? 50 : 500} ${unit})`} />
              {(pending.factor < 0.90 || pending.factor > 1.15) && (
                <div className="rounded-md border border-[#F5821F]/50 bg-[#F5821F]/10 p-2 text-[12px] flex gap-2" data-testid="calib-warning">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-[#F5821F] mt-0.5" />
                  <span>Écart important ({Math.round((pending.factor - 1) * 100)} %). Vérifiez le compteur avant d'appliquer.</span>
                </div>
              )}
              <p className="text-[12px] text-muted-foreground">Appliquer ce coefficient aux {isH ? "heures" : "kilomètres"} futurs ?</p>
              <div className="flex items-center justify-end gap-2">
                <button onClick={keepFactor} data-testid="calib-factor-keep" className="text-sm text-muted-foreground px-3 py-2">Non, garder</button>
                <button disabled={busy} onClick={applyFactor} data-testid="calib-factor-apply"
                  className="text-sm font-bold bg-[#10B981] text-white px-4 py-2 rounded-md disabled:opacity-40">Oui, appliquer</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
